import React from 'react'
import { Chip, Typography, Box, Paper } from '@mui/material'
import { Error as ErrorIcon, Warning as WarningIcon } from '@mui/icons-material'

export interface ValidationError {
  codeErreur: number
  codeRegle: string
  messageErreur: string
  forcable: boolean
  acte?: string
}

interface ValidationErrorsListProps {
  errors: ValidationError[]
  title?: string
  subtitle?: string
}

const ValidationErrorsList: React.FC<ValidationErrorsListProps> = ({
  errors,
  title = 'Validation échouée',
  subtitle,
}) => {
  if (!errors || errors.length === 0) return null

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 3,
        borderRadius: 3,
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #fef2f2 0%, #fff5f5 100%)',
        border: '1px solid',
        borderColor: 'rgba(82, 50, 50, 0.2)',
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid rgba(239, 68, 68, 0.1)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
            }}
          >
            <ErrorIcon sx={{ color: 'white', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography
              variant="subtitle1"
              fontWeight={600}
              color="text.primary"
            >
              {title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {subtitle ||
                `${errors.length} problème${
                  errors.length > 1 ? 's' : ''
                } à résoudre`}
            </Typography>
          </Box>
        </Box>
        <Chip
          label={`${errors.length} erreur${errors.length > 1 ? 's' : ''}`}
          size="small"
          sx={{
            bgcolor: 'rgba(239, 68, 68, 0.1)',
            color: '#dc2626',
            fontWeight: 600,
            border: '1px solid rgba(239, 68, 68, 0.2)',
          }}
        />
      </Box>
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box
          sx={{
            bgcolor: 'white',
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'rgba(0,0,0,0.06)',
          }}
        >
          {errors.map((err, index) => (
            <Box
              key={index}
              sx={{
                px: 2,
                py: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#fee2e2',
                  color: '#dc2626',
                  fontSize: 12,
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                {index + 1}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  {err.acte && (
                    <Chip
                      label={err.acte}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: 11,
                        fontWeight: 600,
                        bgcolor: '#fef3c7',
                        color: '#92400e',
                        border: 'none',
                      }}
                    />
                  )}
                  <Box display={'flex'}>
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      color="text.primary"
                    >
                      {err.codeRegle} :
                    </Typography>
                    <Typography variant="body2" fontWeight={400}>
                      {' '}
                      &nbsp;{err.messageErreur}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Paper>
  )
}

export default ValidationErrorsList
